import { useEffect, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import SmallLogo from '../../assets/logo-white-no-background.png'
import { LEGAL_OPERATOR_ADDRESS_LINES } from '../../content/legalEntity'
import Footer from '../homecomponents/Footer'
import { usePublicSiteBranding } from '../../contexts/PublicSiteContext'
import { getDocumentTitle } from '../../utils/publicSite'

interface LegalPageLayoutProps {
  title: string
  children: ReactNode
}

export default function LegalPageLayout({ title, children }: LegalPageLayoutProps) {
  const branding = usePublicSiteBranding()

  useEffect(() => {
    const prev = document.title
    document.title = `${getDocumentTitle()} — ${title}`
    return () => {
      document.title = prev
    }
  }, [title])

  return (
    <div className="min-h-screen flex flex-col w-full">
      <nav className={`w-full ${branding.homeNavBarClass} text-white p-4`}>
        <div className="container mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <img
              src={branding.markLogoSrc ?? SmallLogo}
              alt={branding.navbarLogoAlt}
              className={branding.navLogoClass ?? 'h-12'}
            />
          </Link>
          <div className="flex space-x-4">
            <Link to="/" className="hover:text-gray-300 text-lg font-medium">
              Home
            </Link>
            <Link to="/login" className="hover:text-gray-300 text-lg font-medium">
              Login
            </Link>
          </div>
        </div>
      </nav>
      <main className="flex-1 w-full bg-white">
        <article className="mx-auto w-full max-w-3xl px-6 py-12 sm:px-8">
          {children}
        </article>
      </main>
      <Footer />
    </div>
  )
}

export function LegalSection({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <section className="mt-10">
      <h2 className="text-xl font-bold text-marketing-ink border-b border-marketing-muted pb-2 mb-4">
        {title}
      </h2>
      <div className="space-y-4 text-base leading-7 text-gray-800">{children}</div>
    </section>
  )
}

export function LegalSubheading({ children }: { children: ReactNode }) {
  return <h3 className="text-lg font-semibold text-marketing-ink pt-2">{children}</h3>
}

export function LegalList({ items }: { items: ReactNode[] }) {
  return (
    <ol className="list-decimal pl-6 space-y-2">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ol>
  )
}

export function LegalAddress() {
  return (
    <address className="not-italic">
      {LEGAL_OPERATOR_ADDRESS_LINES.map((line) => (
        <span key={line} className="block">
          {line}
        </span>
      ))}
    </address>
  )
}
